import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_NAME, SITE_ORIGIN, isKnownAppPath, lookupDiscovery, structuredData } from '../../data/discovery.ts';
import '../../styles/page-guide.css';

function meta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) { element = document.createElement('meta'); element.setAttribute(attribute, key); document.head.append(element); }
  element.content = content;
}

export default function PageMetadata() {
  const { pathname } = useLocation();
  useEffect(() => {
    const page = lookupDiscovery(pathname);
    const title = page?.title ?? `${isKnownAppPath(pathname) ? 'Your app' : 'Page not found'} | ${SITE_NAME}`;
    const description = page?.description ?? 'Personal planning and account features in the independent Lost Lands community app.';
    document.title = title;
    meta('name', 'description', description);
    meta('name', 'robots', page ? 'index, follow, max-image-preview:large' : 'noindex, nofollow');
    meta('property', 'og:title', title); meta('property', 'og:description', description);
    meta('property', 'og:site_name', SITE_NAME);
    meta('name', 'twitter:title', title); meta('name', 'twitter:description', description);
    document.head.querySelectorAll('link[rel="canonical"]').forEach(element => element.remove());
    document.getElementById('discovery-jsonld')?.remove();
    if (page) {
      const link = document.createElement('link'); link.rel = 'canonical'; link.href = SITE_ORIGIN + page.path; document.head.append(link);
      meta('property', 'og:url', link.href);
      const data = document.createElement('script'); data.type = 'application/ld+json'; data.id = 'discovery-jsonld'; data.textContent = JSON.stringify(structuredData(page)); document.head.append(data);
    } else document.head.querySelector('meta[property="og:url"]')?.remove();
  }, [pathname]);
  return null;
}

// The build uses this same editorial copy in the initial HTML for every visitor.
// Keep it available in the interactive app instead of writing crawler-only text.
export function PublicPageGuide() {
  const { pathname } = useLocation();
  const page = lookupDiscovery(pathname);
  if (!page) return null;
  return <details className="page-guide">
    <summary>About this page and its sources</summary>
    <div className="page-guide-sections">{page.sections.map(section => <section key={section.heading}>
      <h2>{section.heading}</h2>
      {section.paragraphs.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
      {section.links && <ul>{section.links.map(link => <li key={link.href}><a href={link.href}>{link.label}</a></li>)}</ul>}
    </section>)}</div>
  </details>;
}
